import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getPageBySlug,
  getAllArticlesByPage,
} from "./../../services/pagesPersonnaliseesServices";
import useScrollToTop from "./../../hooks/useScollToTop";

/* =========================
   Helpers détection des médias
   ========================= */
const isImageUrl = (url = "") =>
  /\.(avif|gif|jpe?g|png|webp|svg)(\?.*)?$/i.test(url);

const isDirectVideoUrl = (url = "") =>
  /\.(mp4|webm|ogg|ogv|mov|m4v|m3u8)(\?.*)?$/i.test(url);

const getYouTubeId = (url = "") => {
  try {
    const u = new URL(url);
    if (/youtu\.be$/i.test(u.hostname)) {
      return u.pathname.slice(1);
    }
    if (/youtube\.com$/i.test(u.hostname)) {
      if (u.pathname.startsWith("/watch")) return u.searchParams.get("v");
      if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/")[2];
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/")[2];
    }
  } catch (_) {}
  return null;
};

const getVimeoId = (url = "") => {
  try {
    const u = new URL(url);
    if (/vimeo\.com$/i.test(u.hostname)) {
      // gère vimeo.com/123456789 ou vimeo.com/channels/xxx/123456789
      const parts = u.pathname.split("/").filter(Boolean);
      const last = parts[parts.length - 1];
      if (/^\d+$/.test(last)) return last;
    }
  } catch (_) {}
  return null;
};

const getDailymotionId = (url = "") => {
  try {
    const u = new URL(url);
    if (/dailymotion\.com$/i.test(u.hostname)) {
      // dailymotion.com/video/{id}
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("video");
      if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
    }
    if (/dai\.ly$/i.test(u.hostname)) {
      // dai.ly/{id}
      const id = u.pathname.slice(1);
      if (id) return id;
    }
  } catch (_) {}
  return null;
};

/* =========================
   Composants média
   ========================= */
const ResponsiveIframe = ({ src, title }) => (
  <div className="w-full rounded-xl shadow-lg overflow-hidden">
    <div className="relative w-full pt-[56.25%]">
      <iframe
        className="absolute inset-0 h-full w-full"
        src={src}
        title={title || "Video"}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </div>
  </div>
);

const Html5Video = ({ src }) => (
  <video
    className="w-full rounded-xl shadow-lg"
    src={src}
    controls
    preload="metadata"
    playsInline
  />
);

const MediaRenderer = ({ url, alt }) => {
  if (!url) return null;

  // 1) Image directe
  if (isImageUrl(url)) {
    return (
      <img
        src={url}
        alt={alt || ""}
        loading="lazy"
        className="w-full rounded-xl shadow-lg transition-transform duration-300 hover:scale-105"
      />
    );
  }

  // 2) Plateformes vidéo
  const ytId = getYouTubeId(url);
  if (ytId) {
    const embed = `https://www.youtube.com/embed/${ytId}?rel=0`;
    return <ResponsiveIframe src={embed} title={alt} />;
  }

  const vmId = getVimeoId(url);
  if (vmId) {
    const embed = `https://player.vimeo.com/video/${vmId}`;
    return <ResponsiveIframe src={embed} title={alt} />;
  }

  const dmId = getDailymotionId(url);
  if (dmId) {
    const embed = `https://www.dailymotion.com/embed/video/${dmId}`;
    return <ResponsiveIframe src={embed} title={alt} />;
  }

  // 3) Fichier vidéo direct
  if (isDirectVideoUrl(url)) {
    return <Html5Video src={url} />;
  }

  // 4) Fallback — tenter en iframe générique (cas rares) ou image
  if (/(video|player|embed|stream|youtube|vimeo|dailymotion)/i.test(url)) {
    return <ResponsiveIframe src={url} title={alt} />;
  }

  // Sinon, fallback image (au cas où URL image sans extension)
  return (
    <img
      src={url}
      alt={alt || ""}
      loading="lazy"
      className="w-full rounded-xl shadow-lg transition-transform duration-300 hover:scale-105"
    />
  );
};

const PagePersonnalisee = () => {
  useScrollToTop();
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const pageData = await getPageBySlug(slug);
      if (!mounted) return;
      setPage(pageData);

      const articlesData = await getAllArticlesByPage(pageData.id);

      if (!mounted) return;

      // ✅ Tri par `ordre` (croissant), puis par `id` si égalité / `ordre` manquant
      const sorted =
        Array.isArray(articlesData)
          ? [...articlesData].sort((a, b) => {
              const ao =
                typeof a?.ordre === "number" ? a.ordre : Number.MAX_SAFE_INTEGER;
              const bo =
                typeof b?.ordre === "number" ? b.ordre : Number.MAX_SAFE_INTEGER;
              if (ao !== bo) return ao - bo;
              const ai = typeof a?.id === "number" ? a.id : 0;
              const bi = typeof b?.id === "number" ? b.id : 0;
              return ai - bi;
            })
          : [];

      setArticles(sorted);
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (!page) return <div>Chargement...</div>;

  return (
    <div className="container mx-auto pt-[150px] px-4 mb-10 font-montserrat">
      <h1 className="text-4xl md:text-5xl font-bold text-center mb-6">
        {page.titre}
      </h1>

      {page.introduction && (
        <div
          className="prose prose-lg text-center mx-auto dark:prose-invert mb-12 max-w-3xl"
          dangerouslySetInnerHTML={{ __html: page.introduction }}
        />
      )}

      <div className="space-y-20 mb-10">
        {articles.map((article, index) => {
          const hasMedia = Boolean(article.url_image); // url_image peut pointer vers une image OU une vidéo
          const hasText = Boolean(article.contenu_texte);

          const rowBase = hasMedia && hasText ? "md:flex-row" : "flex-col";
          const rowReverse =
            index % 2 !== 0 && hasMedia && hasText ? "md:flex-row-reverse" : "";

          return (
            <div
              key={article.id}
              className={`flex flex-col ${rowBase} ${rowReverse} items-center gap-8`}
            >
              {hasMedia && hasText && (
                <>
                  <div className="w-full md:w-1/2">
                    <MediaRenderer
                      url={article.url_image}
                      alt={article.titre_article}
                    />
                  </div>

                  <div className="w-full md:w-1/2 bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                    <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-primary dark:text-orange-400">
                      {article.titre_article}
                    </h2>
                    <div
                      className="prose dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: article.contenu_texte }}
                    />
                  </div>
                </>
              )}

              {hasMedia && !hasText && (
                <div className="w-full">
                  <MediaRenderer
                    url={article.url_image}
                    alt={article.titre_article}
                  />
                </div>
              )}

              {!hasMedia && hasText && (
                <div className="w-full bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                  <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-primary dark:text-orange-400">
                    {article.titre_article}
                  </h2>
                  <div
                    className="prose dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: article.contenu_texte }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PagePersonnalisee;
